import { useEffect, useMemo, useState } from 'react'
import { Button } from '@unified-lk/ui'
import { formatDateTime } from '../../lib/format'
import type { StaffGiftCertificate, StaffGiftCertificateFormInput, StaffGiftCertificatesData } from '../../types/plans'
import { StaffMetricCard } from '../../shared/staff-ui'
import {
  workspaceActionsClassName,
  workspaceCardClassName,
  workspaceEmptyClassName,
  workspaceFieldClassName,
  workspaceFieldLabelClassName,
  workspaceGridClassName,
  workspaceInputClassName,
  workspaceMutedTextClassName,
  workspacePanelClassName,
  workspaceSectionHeaderClassName,
} from '../workspaceStyles'

function toLocalDateTimeValue(value: string | undefined) {
  if (!value) {
    return ''
  }

  const date = new Date(value)
  const offsetMinutes = date.getTimezoneOffset()
  const normalized = new Date(date.getTime() - offsetMinutes * 60_000)

  return normalized.toISOString().slice(0, 16)
}

function toApiDateTimeValue(value: string) {
  return value ? new Date(value).toISOString() : ''
}

function certificateCardBackground(preview: StaffGiftCertificate | null, primaryColor: string | undefined) {
  const backgroundImageUrl = preview?.theme.backgroundImageUrl
  return backgroundImageUrl
    ? `linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.96)), url("${backgroundImageUrl}")`
    : `linear-gradient(135deg, color-mix(in srgb, ${primaryColor ?? '#0b74de'} 12%, white), white)`
}

export function StaffGiftCertificatesSection({
  data,
  loading,
  error,
  onRetry,
  onPreview,
  onIssue,
  onSearchPatients,
}: {
  data: StaffGiftCertificatesData | null
  loading: boolean
  error: string | null
  onRetry: () => void
  onPreview: (input: StaffGiftCertificateFormInput) => Promise<StaffGiftCertificate>
  onIssue: (input: StaffGiftCertificateFormInput) => Promise<StaffGiftCertificate>
  onSearchPatients: (query: string) => Promise<Array<{ id: number; full_name: string; phone?: string | null; phone_hidden?: boolean }>>
}) {
  const templates = data?.templates ?? []
  const defaultTemplate = templates[0] ?? null
  const [form, setForm] = useState<StaffGiftCertificateFormInput>({
    template_id: defaultTemplate?.id ?? '',
    patient_id: null,
    patient_name: '',
    amount: null,
    offer_text: defaultTemplate?.offer_text ?? '',
    support_text: defaultTemplate?.support_text ?? '',
    expires_at: '',
  })
  const [patientSearch, setPatientSearch] = useState('')
  const [patientResults, setPatientResults] = useState<Array<{ id: number; full_name: string; phone?: string | null; phone_hidden?: boolean }>>([])
  const [searchingPatients, setSearchingPatients] = useState(false)
  const [preview, setPreview] = useState<StaffGiftCertificate | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!defaultTemplate) {
      return
    }

    setForm((previous) => previous.template_id
      ? previous
      : {
          ...previous,
          template_id: defaultTemplate.id,
          offer_text: defaultTemplate.offer_text,
          support_text: defaultTemplate.support_text,
        })
  }, [defaultTemplate])

  useEffect(() => {
    const query = patientSearch.trim()
    if (query.length < 2) {
      setPatientResults([])
      return
    }

    const timeout = window.setTimeout(() => {
      setSearchingPatients(true)
      void onSearchPatients(query)
        .then(setPatientResults)
        .catch(() => setPatientResults([]))
        .finally(() => setSearchingPatients(false))
    }, 250)

    return () => window.clearTimeout(timeout)
  }, [onSearchPatients, patientSearch])

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === form.template_id) ?? defaultTemplate,
    [defaultTemplate, form.template_id, templates],
  )

  const amountHint = useMemo(() => {
    if (!form.amount || form.amount <= 0) {
      return '1–99 — процент, 100+ — рубли'
    }

    return form.amount < 100
      ? `Будет сохранено как скидка ${form.amount}%.`
      : `Будет сохранено как сертификат на сумму ${new Intl.NumberFormat('ru-RU').format(form.amount)} ₽.`
  }, [form.amount])

  if (loading) {
    return <div className={workspaceEmptyClassName}><p>Загружаем шаблоны сертификатов...</p></div>
  }

  if (!data) {
    return (
      <div className={workspaceEmptyClassName}>
        <p>{error ?? 'Раздел сертификатов пока недоступен.'}</p>
        <div className={workspaceActionsClassName}>
          <Button variant="secondary" type="button" onClick={onRetry}>Повторить</Button>
        </div>
      </div>
    )
  }

  async function handlePreview() {
    setSaving(true)
    setMessage(null)

    try {
      const nextPreview = await onPreview(form)
      setPreview(nextPreview)
      setMessage('Предпросмотр обновлён.')
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : 'Не удалось собрать preview.')
    } finally {
      setSaving(false)
    }
  }

  async function handleIssue() {
    setSaving(true)
    setMessage(null)

    try {
      const issued = await onIssue(form)
      setPreview(issued)
      setMessage('Сертификат выпущен и сохранён.')
      onRetry()
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : 'Не удалось выпустить сертификат.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={workspaceGridClassName}>
      <section className={workspacePanelClassName}>
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Сертификаты</h2>
            <p className={workspaceMutedTextClassName}>Подарочные сертификаты на услуги клиники</p>
          </div>
          <Button variant="secondary" type="button" onClick={onRetry}>Обновить</Button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StaffMetricCard label="Шаблонов" value={data.summary.templates_total} accent="blue" />
          <StaffMetricCard label="Выпущено" value={data.summary.issued_total} accent="green" />
          <StaffMetricCard label="Активно" value={data.summary.active_total} accent="purple" />
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">Новый сертификат</h3>

          <div className="grid gap-4">
            <label className={workspaceFieldClassName}>
              <span className={workspaceFieldLabelClassName}>Шаблон</span>
              <select
                className={workspaceInputClassName}
                value={form.template_id}
                onChange={(event) => {
                  const template = templates.find((item) => item.id === event.target.value)
                  setForm((previous) => ({
                    ...previous,
                    template_id: event.target.value,
                    offer_text: template?.offer_text ?? previous.offer_text,
                    support_text: template?.support_text ?? previous.support_text,
                  }))
                }}
              >
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>{template.title}</option>
                ))}
              </select>
            </label>

            <label className={workspaceFieldClassName}>
              <span className={workspaceFieldLabelClassName}>Пациент</span>
              <input
                className={workspaceInputClassName}
                value={patientSearch}
                onChange={(event) => {
                  setPatientSearch(event.target.value)
                  setForm((previous) => ({ ...previous, patient_id: null, patient_name: '' }))
                }}
                placeholder="Начните вводить ФИО..."
              />
              {form.patient_name ? <small className="text-xs text-slate-500">Выбран: {form.patient_name}</small> : null}
              {searchingPatients ? <small className="text-xs text-slate-500">Ищем пациента...</small> : null}
              {!form.patient_name && patientResults.length > 0 ? (
                <div className="grid gap-2">
                  {patientResults.map((patient) => (
                    <button
                      key={patient.id}
                      type="button"
                      className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm hover:border-blue-200 hover:bg-blue-50"
                      onClick={() => {
                        setForm((previous) => ({
                          ...previous,
                          patient_id: patient.id,
                          patient_name: patient.full_name,
                        }))
                        setPatientSearch(patient.full_name)
                        setPatientResults([])
                      }}
                    >
                      <strong className="text-slate-900">{patient.full_name}</strong>
                      <span className="ml-2 text-slate-500">#{patient.id}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </label>

            <label className={workspaceFieldClassName}>
              <span className={workspaceFieldLabelClassName}>Сумма / скидка</span>
              <input
                className={workspaceInputClassName}
                type="number"
                min={1}
                value={form.amount ?? ''}
                onChange={(event) => setForm((previous) => ({
                  ...previous,
                  amount: event.target.value ? Number(event.target.value) : null,
                }))}
                placeholder="Например 10 или 5000"
              />
              <small className="text-xs text-slate-500">{amountHint}</small>
            </label>

            <label className={workspaceFieldClassName}>
              <span className={workspaceFieldLabelClassName}>Оффер</span>
              <textarea
                className={workspaceInputClassName}
                rows={4}
                value={form.offer_text}
                onChange={(event) => setForm((previous) => ({ ...previous, offer_text: event.target.value }))}
              />
            </label>

            <label className={workspaceFieldClassName}>
              <span className={workspaceFieldLabelClassName}>Срок действия</span>
              <input
                className={workspaceInputClassName}
                type="datetime-local"
                value={toLocalDateTimeValue(form.expires_at)}
                onChange={(event) => setForm((previous) => ({ ...previous, expires_at: toApiDateTimeValue(event.target.value) }))}
              />
            </label>

            <div className={workspaceActionsClassName}>
              <Button type="button" onClick={() => void handlePreview()} disabled={saving || !form.template_id || !form.patient_id || !form.amount}>
                {saving ? 'Собираем...' : 'Предпросмотр'}
              </Button>
              <Button variant="secondary" type="button" onClick={() => void handleIssue()} disabled={saving || !form.template_id || !form.patient_id || !form.amount}>
                Выпустить
              </Button>
            </div>

            {message ? <p className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">{message}</p> : null}
          </div>
        </div>

        <div className="grid gap-3">
          <div
            className="rounded-xl border border-emerald-100 px-5 py-6 shadow-sm"
            style={{
              ['--certificate-primary' as string]: preview?.theme.primary ?? selectedTemplate?.theme.primary ?? '#0b74de',
              backgroundImage: certificateCardBackground(preview, preview?.theme.primary ?? selectedTemplate?.theme.primary),
            }}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--certificate-primary)]">
              {preview?.brand_name ?? selectedTemplate?.brand_name ?? 'Клиника'}
            </p>
            <h3 className="mt-2 text-2xl font-bold text-slate-900">{preview?.template_title ?? selectedTemplate?.title ?? 'Классический'}</h3>
            <p className="mt-4 text-xs uppercase tracking-wide text-slate-500">Получатель</p>
            <p className="text-base font-semibold text-slate-900">{preview?.recipient_name || form.patient_name || '—'}</p>
            <p className="mt-4 text-2xl font-bold text-slate-900">{preview?.amount_label || 'Сумма / скидка'}</p>
            <p className="mt-4 text-sm leading-6 text-slate-600">{preview?.offer_text || form.offer_text}</p>
            {preview ? <small className="mt-4 block text-xs text-slate-500">Действует до: {formatDateTime(preview.expires_at)}</small> : null}
          </div>
          <p className="text-center text-xs text-slate-400">Предварительный вид</p>
        </div>
      </section>

      <section className={workspacePanelClassName}>
        <div className={workspaceSectionHeaderClassName}>
          <h3 className="text-xl font-semibold text-slate-900">Выпущенные сертификаты</h3>
          <span className="text-sm text-slate-500">{data.issued.length}</span>
        </div>
        <div className="grid gap-4 xl:grid-cols-2">
          {data.issued.map((certificate) => (
            <article key={certificate.id} className={workspaceCardClassName}>
              <div className={workspaceSectionHeaderClassName}>
                <strong className="text-slate-900">{certificate.recipient_name}</strong>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">{certificate.amount_label}</span>
              </div>
              <p className={workspaceMutedTextClassName}>{certificate.template_title}</p>
              <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                <span>{certificate.status}</span>
                <span>до {formatDateTime(certificate.expires_at)}</span>
              </div>
              {certificate.share?.public_url ? (
                <div className={workspaceActionsClassName}>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(certificate.share?.public_url ?? '')}
                  >
                    Копировать ссылку
                  </Button>
                  <Button
                    type="button"
                    onClick={() => window.open(certificate.share?.public_url ?? '#', '_blank', 'noopener,noreferrer')}
                  >
                    Открыть
                  </Button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}
